import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ContactInbox = ({ token }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/contact-messages`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setMessages(res.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load messages.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div data-testid="contact-inbox" className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Contact Inbox</h2>
            <p className="text-sm text-gray-600 mt-1">
              Messages submitted via the public <code>/contact</code> form.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            data-testid="inbox-refresh"
            className="px-4 py-2 text-sm font-semibold rounded-md border border-gray-300 hover:bg-gray-50"
          >
            Refresh
          </button>
        </div>
        {error && (
          <div className="mt-4 px-4 py-2.5 rounded-md text-sm bg-red-50 text-red-700 border border-red-200">
            {error}
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500" data-testid="inbox-loading">
            Loading messages…
          </div>
        ) : messages.length === 0 ? (
          <div className="p-10 text-center text-gray-500" data-testid="inbox-empty">
            No contact messages yet.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Received
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  From
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Subject
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {messages.map((m) => (
                <tr key={m.id} data-testid={`inbox-row-${m.id}`} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                    {m.created_at ? new Date(m.created_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="font-medium text-gray-900">{m.name || '—'}</div>
                    <div className="text-xs text-gray-500">{m.email || '—'}</div>
                    {m.phone && <div className="text-xs text-gray-500">{m.phone}</div>}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">
                    {m.subject || '(no subject)'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{m.organization || '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => setSelected(m)}
                      data-testid={`inbox-view-${m.id}`}
                      className="text-sm font-semibold text-blue-600 hover:underline"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            data-testid="inbox-detail"
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="px-6 py-4 border-b flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-lg font-semibold">{selected.subject || '(no subject)'}</h3>
              <button
                onClick={() => setSelected(null)}
                data-testid="inbox-detail-close"
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-gray-500">From</div>
                  <div className="font-medium">{selected.name || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Received</div>
                  <div>{selected.created_at ? new Date(selected.created_at).toLocaleString() : '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Email</div>
                  <a
                    href={`mailto:${selected.email}`}
                    className="text-blue-600 hover:underline"
                  >
                    {selected.email}
                  </a>
                </div>
                <div>
                  <div className="text-xs text-gray-500">Phone</div>
                  <div>{selected.phone || '—'}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-xs text-gray-500">Organization</div>
                  <div>{selected.organization || '—'}</div>
                </div>
              </div>
              <div className="pt-3 border-t">
                <div className="text-xs text-gray-500 mb-1">Message</div>
                <div className="text-sm text-gray-800 whitespace-pre-wrap">
                  {selected.message || '(no body)'}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-2 bg-gray-50">
              <a
                href={`mailto:${selected.email}?subject=Re: ${encodeURIComponent(selected.subject || '')}`}
                data-testid="inbox-reply"
                className="px-4 py-2 text-sm font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700"
              >
                Reply via email
              </a>
              <button
                onClick={() => setSelected(null)}
                className="px-4 py-2 text-sm font-semibold rounded-md border border-gray-300 hover:bg-white"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactInbox;
